import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import WithNavBar from './components/Layout/WithNavBar';
import WithoutNavBar from './components/Layout/WithoutNavBar';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import MobileVerificationWrapper from './components/OTPVerification/MobileVerificationWrapper';
import { Financial } from './pages/Financial';
import { Medical } from './pages/Medical';
import { Visits } from './pages/Visits';
import { Vitals } from './pages/Vitals';
import { VitalDetails } from './pages/VitalDetails';
import { VitalAllData } from './pages/VitalAllData';
import { Claims } from './pages/Claims';
import { Bills } from './pages/Bills';
import { Medications } from './pages/Medications';
import { Procedures } from './pages/Procedures';
import { Allergies } from './pages/Allergies';
import { LabResults } from './pages/LabResults';
import { Documents } from './pages/Documents';
import { Immunizations } from './pages/Immunizations';
import { Conditions } from './pages/Conditions';
import { Formulary } from './pages/Formulary';
import { SmokingStatus } from './pages/SmokingStatus';
import { PriorAuthorization } from './pages/PriorAuthorization';
import { Coverage } from './pages/Coverage';
import { Care } from './pages/Care';
import { CarePlans } from './pages/CarePlans';
import { FindCare } from './pages/FindCare';
import { CareTeam } from './pages/CareTeam';
import { Appointments } from './pages/Appointments';
import { Assessments } from './pages/Assessments';
import { Legal } from './pages/Legal';
import { Consent } from './pages/Consent';
import { AdvanceDirective } from './pages/AdvanceDirective';
import { Account } from './pages/Account';
import { AccountDetails } from './pages/AccountDetails';
import { PlanCoverage } from './pages/PlanCoverage';
import { ExportRecords } from './pages/ExportRecords';
import CoveragePriceList from './components/CoveragePriceListComponent/CoveragePriceList';
import ActivePlansPriceList from './pages/ActivePlansPriceList';
import ActivePlansPriceListWrapper from './pages/ActivePlansPriceListWrapper';
import { BacShoppingModule } from './pages/BacShoppingModule';
import { AssessmentPage } from './pages/AssessmentPage';
import { ViewAssessment } from './components/ViewAssessment/ViewAssessment';

const RequireAuth = ({
  isAuthenticated,
  children,
  redirectTo,
  shouldSaveCurrentPath,
}) => {
  let location = useLocation();
  if (shouldSaveCurrentPath && !isAuthenticated) {
    sessionStorage.setItem(
      'redirectPathAfterLogin',
      location.pathname + location.search
    );
  }
  return isAuthenticated ? children : <Navigate to={redirectTo} />;
};

const DefaultComponent = ({ isAuthenticated }) => {
  if (JSON.parse(sessionStorage.getItem('BAC_SHOPPING_EXP'))) {
    return <Navigate to={'/sessionExpired'} />;
  }

  return isAuthenticated ? (
    <Navigate to={'/dashboard'} />
  ) : (
    <Navigate to={'/login'} />
  );
};

const BacSessionExpiredComponent = () => {
  return <NotFound bacTokenExpiry={true} />;
};

const DoesntRequireAuth = ({ isAuthenticated, children, redirectTo }) => {
  let redirectURL = sessionStorage.getItem('redirectPathAfterLogin');
  return isAuthenticated ? (
    <Navigate to={redirectURL || redirectTo} />
  ) : (
    children
  );
};

const RootRoute = ({ isAuthenticated }) => {
  return (
    <Routes>
      <Route
        path="/"
        element={<DefaultComponent isAuthenticated={isAuthenticated} />}
      />
      <Route element={<WithoutNavBar />}>
        <Route
          path="/login"
          element={
            <DoesntRequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/dashboard">
              <Login />
            </DoesntRequireAuth>
          }
        />
        <Route
          path="/sessionExpired"
          element={<BacSessionExpiredComponent />}
        />
        <Route
          path="/signup"
          element={
            <DoesntRequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/dashboard">
              <Signup />
            </DoesntRequireAuth>
          }
        />
        <Route
          path="/otpverification"
          element={
            <DoesntRequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/dashboard">
              <MobileVerificationWrapper />
            </DoesntRequireAuth>
          }
        />
        <Route
          path="/forgotPassword"
          element={
            <DoesntRequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/dashboard">
              <ForgotPassword />
            </DoesntRequireAuth>
          }
        />
        <Route
          path="/activeplanspricelist"
          element={
            <DoesntRequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/dashboard">
              <ActivePlansPriceListWrapper />
            </DoesntRequireAuth>
          }
        />
        <Route
          path="/find-care/*"
          element={
            <DoesntRequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/sessionExpired">
              <BacShoppingModule />
            </DoesntRequireAuth>
          }
        />
      </Route>
      <Route element={<WithNavBar />}>
        <Route
          path="/dashboard"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Dashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/medical"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Medical />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/visits"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Visits />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/medications"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Medications />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/procedures"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Procedures />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/allergies"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Allergies />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/labResults"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <LabResults />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/documents"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Documents />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/immunizations"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Immunizations />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/conditions"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Conditions />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/formulary"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Formulary />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/socialHistory"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <SmokingStatus />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/vitals"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Vitals />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/vitals/:vitalType"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <VitalDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/medical/vitals/:vitalType/alldata"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <VitalAllData />
            </RequireAuth>
          }
        />
        <Route
          path="/financial"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Financial />
            </RequireAuth>
          }
        />
        <Route
          path="/financial/claims"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Claims />
            </RequireAuth>
          }
        />
        <Route
          path="/financial/bills"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Bills />
            </RequireAuth>
          }
        />
        <Route
          path="/financial/priorAuthorization"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <PriorAuthorization />
            </RequireAuth>
          }
        />
        <Route
          path="/financial/coverage"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Coverage />
            </RequireAuth>
          }
        />
        <Route
          path="/financial/coverage/coveragepricelist/:id"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <CoveragePriceList />
            </RequireAuth>
          }
        />
        <Route
          path="/care"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Care />
            </RequireAuth>
          }
        />
        <Route
          path="/care/carePlans"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <CarePlans />
            </RequireAuth>
          }
        />
        <Route
          path="/care/findCare/*"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <FindCare />
            </RequireAuth>
          }
        />
        <Route
          path="/care/careTeam"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <CareTeam />
            </RequireAuth>
          }
        />
        <Route
          path="/care/appointments"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Appointments />
            </RequireAuth>
          }
        />
        <Route
          path="/care/assessments"
          element={
            <RequireAuth
              isAuthenticated={isAuthenticated}
              redirectTo="/login"
              shouldSaveCurrentPath={window.location.href.includes('?assessmentSubmissionId')}>
              <AssessmentPage />
            </RequireAuth>
          }
        />
        {/* <Route path="/care/assessments" element={<AssessmentPage />} /> */}
        <Route
          path="/legal"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Legal />
            </RequireAuth>
          }
        />
        <Route
          path="/legal/consents"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Consent />
            </RequireAuth>
          }
        />
        <Route
          path="/legal/advanceDirective"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <AdvanceDirective />
            </RequireAuth>
          }
        />
        <Route
          path="/account"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <Account />
            </RequireAuth>
          }
        />
        <Route
          path="/account/accountDetails"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <AccountDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/account/planCoverage"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <PlanCoverage />
            </RequireAuth>
          }
        />
        <Route
          path="/account/exportRecords"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <ExportRecords />
            </RequireAuth>
          }
        />
        <Route
          path="/assessment"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <AssessmentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/viewAssessment"
          element={
            <RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login">
              <ViewAssessment />
            </RequireAuth>
          }
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default RootRoute;
